
import { Component, Vue } from 'vue-property-decorator';

import { sharedState } from '../../../../framework/state';

import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';
import { Account } from '../../../../../common/application/model/Account';
import { getCustomerOptions } from '../../../service/application_options_service';
import { getEnumerationOptions, getOptionLabel, OptionValue } from '../../../../framework/service/options';
import SelectField from '../../../../framework/fields/SelectField.vue';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { Customer } from '../../../../../common/application/model/Customer';
import SelectComponent from '../../../../framework/fields/SelectComponent.vue';
import { AccountType } from '../../../../../common/application/model/enums/AccountType';
import { errorToast, successToast } from '../../../service/toast_service';
import ParameterService from '../../../service/parameter_service';
import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';

@Component({
    components: { AutoCompleteMultipleField, SelectComponent, SelectField },
})
export default class Accounts extends Vue {
    readonly resourceType = ApplicationResource.ACCOUNT;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as Account[],
        total: 0,
        loading: false,
        selected: undefined as Account | undefined,
        accountTypeOptions: [] as OptionValue[],
        filterCustomerOptions: [] as OptionValue[],
        customerOptions: [] as OptionValue[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.filterCustomerOptions = [
            { id: 'ALL' as string | undefined, label: this.$t('label.all') as string },
        ].concat(
            ((await getResourcesNew(ApplicationResource.CUSTOMER, -1)) as Customer[]).map((o) => {
                return { id: o.id, label: o.name };
            }),
        );
        this.local.customerOptions = await getCustomerOptions();
        this.local.accountTypeOptions = getEnumerationOptions('AccountType', Object.keys(AccountType), this);
        await this.loadAsyncData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    add() {
        this.$router.push(`/${this.resourceType}/add`);
    }

    edit() {
        if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;
        this.local.rows = [];
        const rows = (await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        )) as Array<Account>;
        rows.forEach((row) => {
            this.local.rows.push(row);
        });
        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }
        this.local.loading = false;
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    getAccountTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.accountTypeOptions);
    }

    onRowClick(row: Account): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get customerIds(): string[] {
        return this.local.parameters.customerId || [];
    }

    customerSelectorParameters(): Map<string, string> {
        const parameters = new Map<string, string>();

        if (this.local.parameters.customerType) {
            parameters.set('customerType', this.local.parameters.customerType);
        }

        return parameters;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
